import React, { Dispatch, SetStateAction, useState } from "react";
import { Box, IconButton, Button, Select, MenuItem } from "@mui/material";
import TableComponent from "../../components/TableComponent";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { VenueType } from "../../types/General";
import {
  useAcceptRejectVenueStorageMutation,
  useChangeVenueStorageStatusMutation,
  useDeleteVenueMutation,
  useLazyVenueExportQuery,
} from "../../services/venue";
import { showError, showToast } from "../../constants/toasts";
import { ConfirmationModal } from "../../Modals/ConfirmationModal";
import CustomToggle from "../../helpers/CustomToggle";
import RejectReasonModal from "../../Modals/rejectReason";

interface VenueOwnersListProps {
  type: "pending" | "accepted" | "rejected";
  venueData: VenueType[];
  getVenueOwnerApi: () => void;
  handlePageChange: any;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  count: number;
  venueStorageType: string;
}

const VenueOwnersList: React.FC<VenueOwnersListProps> = ({
  type,
  venueData,
  getVenueOwnerApi,
  page,
  setPage,
  handlePageChange,
  count,
  venueStorageType,
}) => {
  const navigate = useNavigate();
  const [updatedStatus] = useChangeVenueStorageStatusMutation();
  const [changeStatus] = useAcceptRejectVenueStorageMutation();
  const [deleteVenue] = useDeleteVenueMutation();
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedVenueId, setSelectedVenueId] = useState("");
  const isVenue = venueStorageType === "Venue";
  const label = { inputProps: { "aria-label": "Switch demo" } };

  let rows: any[] = [];
  let columns: GridColDef<VenueType>[] = [];

  const deleteVenueApi = async (id: string) => {
    try {
      const response = await deleteVenue({
        id,
      }).unwrap();
      if (response?.statusCode === 200) {
        showToast(response?.message || "");
        getVenueOwnerApi();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const changeVenueStatus = async (
    id: string,
    status: string,
    rejectionReason?: string
  ) => {
    const body = {
      status: status,
      ...(status == "APPROVED"
        ? {}
        : { rejectionReason: rejectionReason || "" }),
    };
    try {
      const response = await changeStatus({
        body,
        id,
      } as any).unwrap();
      if (response.statusCode === 200) {
        showToast(response?.message || "-");
        getVenueOwnerApi();
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const handleRejectClick = (id: string) => {
    setSelectedVenueId(id);
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = (rejectionReason: string) => {
    changeVenueStatus(selectedVenueId, "REJECTED", rejectionReason);
    setRejectModalOpen(false);
  };

  const handleStatusUpdate = async (
    id: string,
    status: boolean,
    type: number
  ) => {
    const body = {
      ...(type === 1 ? { isRecommended: status } : { visibilityTogle: status }),
    };
    try {
      const response = await updatedStatus({ body, id }).unwrap();
      if (response?.statusCode === 200) {
        showToast(response?.message || "");
        // showToast(
        //   type === 1
        //     ? `${venueStorageType} ${
        //         !status ? "marked" : "unmarked"
        //       } recommended successfully`
        //     : `${venueStorageType} ${
        //         !status ? "activated" : "deactivated"
        //       } successfully`
        // );
        getVenueOwnerApi();
      } else {
        showError("Please try again");
      }
    } catch (error: any) {
      console.log(error, "error");
      showError(error?.data?.message || "");
    }
  };

  if (type === "pending") {
    rows = [
      {
        id: 1,
        name: "John Doe",
        email: "john.doe@example.com",
        venue: "Venue A",
        date: "2024-07-26",
      },
      {
        id: 2,
        name: "Jane Smith",
        email: "jane.smith@example.com",
        venue: "Venue B",
        date: "2024-07-27",
      },
    ];
    columns = [
      {
        flex: 1,
        minWidth: 220,
        field: "name",
        headerName: "Name",
        editable: false,
        sortable: false,
        renderCell: ({ row }) => (
          <div className="user_block click_action">
            <p>
              <b>{row?.name || "-"}</b>
            </p>
          </div>
        ),
      },
      {
        flex: 1,
        minWidth: 200,
        field: "category",
        headerName: "Category",
        editable: false,
        sortable: false,
        renderCell: ({ row }) => {
          const categoryNames = Array.isArray(row?.categories)
            ? row.categories.map((c: any) => c?.name).filter(Boolean).join(", ")
            : row?.category?.name;
          return (
            <div className="user_block click_action">
              <p>
                <b>{categoryNames || "-"}</b>
              </p>
            </div>
          );
        },
      },
      {
        flex: 1,
        minWidth: 150,
        field: "price",
        headerName: "Price",
        editable: false,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <div className="user_block click_action">
            <p>
              <b>{`$ ${row?.setPricing?.perDay}`}</b>
            </p>
          </div>
        ),
      },
      {
        flex: 1,
        minWidth: 150,
        field: "guests",
        headerName: isVenue ? "Guests Allowed" : "Capacity(Sq. Ft.)",
        editable: false,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          return (
            <div className="user_block click_action">
              <p>
                <b>{`${isVenue ? row?.guestAllowed : row?.capacity || "-"}`}</b>
              </p>
            </div>
          );
        },
      },

      {
        flex: 1,
        minWidth: 180,
        field: "status",
        headerName: "Status",
        editable: false,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => {
          const current = String(row?.status || "PENDING").toUpperCase();
          return (
            <Select
              value={current}
              onChange={(e) => {
                const selectedValue = String(e.target.value).toUpperCase();
                if (selectedValue === "REJECTED") {
                  setSelectedVenueId(row?._id);
                  setRejectModalOpen(true);
                } else {
                  changeVenueStatus(row?._id, selectedValue);
                }
              }}
              size="small"
            >
              <MenuItem value="PENDING">PENDING</MenuItem>
              <MenuItem value="APPROVED">APPROVED</MenuItem>
              <MenuItem value="REJECTED">REJECTED</MenuItem>
            </Select>
          );
        },
      },
      {
        minWidth: 130,
        field: "action",
        headerName: "Action",
        editable: false,
        sortable: false,
        renderCell: ({ row }) => (
          <div className="table_actions v2">
            <Box>
              <IconButton
                onClick={() =>
                  navigate(
                    `/manage-venue-owners/details/${row?._id}?type=${
                      isVenue ? "Venue" : "Storage"
                    }`
                  )
                }
              >
                <img src="/static/images/eye_icon.svg" alt="" />
              </IconButton>
            </Box>
            <Box>
              <IconButton
                onClick={() =>
                  navigate(`/manage-venue-owners/edit/${row?._id}`)
                }
              >
                <img src="/static/images/edit2_icon.svg" alt="" />
              </IconButton>
            </Box>
            <Box>
              <IconButton
                onClick={() => {
                  setOpen(true);
                  setSelectedId(row?._id);
                }}
              >
                <img src="/static/images/trash2_icon.svg" alt="" />
              </IconButton>
            </Box>
          </div>
        ),
      },
    ];
  } else if (type === "accepted") {
    rows = [
      {
        id: 1,
        name: "Alice Johnson",
        email: "alice.j@example.com",
        venue: "Venue C",
        bookings: 15,
      },
      {
        id: 2,
        name: "Bob Williams",
        email: "bob.w@example.com",
        venue: "Venue D",
        bookings: 25,
      },
    ];
    columns = [
      {
        flex: 1,
        minWidth: 220,
        field: "name",
        headerName: "Name",
        editable: false,
        sortable: false,
        renderCell: (params) => (
          <div className="user_block click_action">
            <p>
              <b>{params.row.name}</b>
            </p>
          </div>
        ),
      },
      {
        flex: 1,
        minWidth: 200,
        field: "category",
        headerName: "Category",
        editable: false,
        sortable: false,
        renderCell: ({ row }) => {
          const categoryNames = Array.isArray(row?.categories)
            ? row.categories.map((c: any) => c?.name).filter(Boolean).join(", ")
            : row?.category?.name;
          return (
            <div className="user_block click_action">
              <p>
                <b>{categoryNames || "-"}</b>
              </p>
            </div>
          );
        },
      },
      {
        flex: 1,
        minWidth: 150,
        field: "price",
        headerName: "Price",
        editable: false,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <div className="user_block click_action">
            <p>
              <b>{`$ ${row?.setPricing?.perDay}`}</b>
            </p>
          </div>
        ),
      },
      {
        flex: 1,
        minWidth: 150,
        field: "guests",
        headerName: isVenue ? "Guests Allowed" : "Capacity(Sq. Ft.)",
        editable: false,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <div className="user_block click_action">
            <p>
              <b>{`${isVenue ? row?.guestAllowed : row?.capacity || "-"}`}</b>
            </p>
          </div>
        ),
      },
      {
        flex: 1,
        minWidth: 150,
        field: "commission",
        headerName: "Commission",
        editable: false,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: () => (
          <Button className="btn sm btn_primary">Manage</Button>
        ),
      },
      {
        flex: 1,
        minWidth: 100,
        field: "recommended",
        headerName: "Recommended",
        editable: false,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const { row, id } = params;
          return (
            <CustomToggle
              checked={!row?.isRecommended}
              onChange={() => {
                handleStatusUpdate(row?._id, !row?.isRecommended, 1);
              }}
              id={row?._id} // size="small"
            />
          );
        },
      },

      {
        flex: 1,
        minWidth: 100,
        field: "status",
        headerName: "Enable/Disable",
        editable: false,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const { row, id } = params;
          return (
            <CustomToggle
              checked={!row?.visibilityTogle}
              onChange={() => {
                handleStatusUpdate(row?._id, !row?.visibilityTogle, 2);
              }}
              id={row?.name} // size="small"
            />
          );
        },
      },

      {
        minWidth: 130,
        field: "action",
        headerName: "Action",
        editable: false,
        sortable: false,
        renderCell: ({ row }) => (
          <div className="table_actions v2">
            <Box>
              <IconButton
                onClick={() =>
                  navigate(
                    `/manage-venue-owners/details/${row?._id}?type=${
                      isVenue ? "Venue" : "Storage"
                    }`
                  )
                }
              >
                <img src="/static/images/eye_icon.svg" alt="" />
              </IconButton>
            </Box>
            <Box>
              <IconButton
                onClick={() =>
                  navigate(`/manage-venue-owners/edit/${row?._id}`)
                }
              >
                <img src="/static/images/edit2_icon.svg" alt="" />
              </IconButton>
            </Box>
            <Box>
              <IconButton
                onClick={() => {
                  setOpen(true);
                  setSelectedId(row?._id);
                }}
              >
                <img src="/static/images/trash2_icon.svg" alt="" />
              </IconButton>
            </Box>
          </div>
        ),
      },
    ];
  } else if (type === "rejected") {
    rows = [
      {
        id: 1,
        name: "Charlie Brown",
        email: "charlie.b@example.com",
        venue: "Venue E",
        reason: "Invalid documents",
      },
      {
        id: 2,
        name: "David Miller",
        email: "david.m@example.com",
        venue: "Venue F",
        reason: "Incomplete application",
      },
    ];
    columns = [
      {
        flex: 1,
        minWidth: 220,
        field: "name",
        headerName: "Name",
        editable: false,
        sortable: false,
        renderCell: (params) => (
          <div className="user_block click_action">
            <p>
              <b>{params.row.name}</b>
            </p>
          </div>
        ),
      },
      {
        flex: 1,
        minWidth: 200,
        field: "category",
        headerName: "Category",
        editable: false,
        sortable: false,
        renderCell: ({ row }) => {
          const categoryNames = Array.isArray(row?.categories)
            ? row.categories.map((c: any) => c?.name).filter(Boolean).join(", ")
            : row?.category?.name;
          return (
            <div className="user_block click_action">
              <p>
                <b>{categoryNames || "-"}</b>
              </p>
            </div>
          );
        },
      },
      {
        flex: 1,
        minWidth: 150,
        field: "price",
        headerName: "Price",
        editable: false,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <div className="user_block click_action">
            <p>
              <b>{`$ ${row?.setPricing?.perDay}`}</b>
            </p>
          </div>
        ),
      },
      {
        flex: 1,
        minWidth: 150,
        field: "guests",
        headerName: isVenue ? "Guests Allowed" : "Capacity(Sq. Ft.)",
        editable: false,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <div className="user_block click_action">
            <p>
              <b>{`${isVenue ? row?.guestAllowed : row?.capacity || "-"}`}</b>
            </p>
          </div>
        ),
      },
      // {
      //   flex: 1,
      //   minWidth: 200,
      //   field: "reason",
      //   headerName: "Reason for Rejection",
      //   editable: false,
      //   sortable: false,
      //   align: "center",
      //   headerAlign: "center",
      // },
      {
        minWidth: 130,
        field: "action",
        headerName: "Action",
        editable: false,
        sortable: false,
        renderCell: ({ row }) => (
          <div className="table_actions v2">
            <Box>
              <IconButton
                onClick={() =>
                  navigate(
                    `/manage-venue-owners/details/${row?._id}?type=${
                      isVenue ? "Venue" : "Storage"
                    }`
                  )
                }
              >
                <img src="/static/images/eye_icon.svg" alt="" />
              </IconButton>
            </Box>
            <Box>
              <IconButton
                onClick={() => {
                  setOpen(true);
                  setSelectedId(row?._id);
                }}
              >
                <img src="/static/images/trash2_icon.svg" alt="" />
              </IconButton>
            </Box>
          </div>
        ),
      },
    ];
  }

  return (
    <div className="sorting_table">
      <TableComponent
        columns={columns}
        rows={venueData}
        count={count}
        page={page}
        onPageChange={handlePageChange}
      />

      <ConfirmationModal
        text={`Are you sure you want to delete this ${venueStorageType}?`}
        openModal={open}
        setOpenModal={setOpen}
        handleSubmit={() => deleteVenueApi(selectedId)}
      />

      

      <RejectReasonModal
        open={rejectModalOpen}
        setOpen={setRejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onSubmit={handleRejectConfirm}
      />
    </div>
  );
};

export default VenueOwnersList;
